import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import FullScreenLoader from "../../../components/common/FullScreenLoader";
import { redirectToServerDown } from "../../../utils/serverStatus";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const AUTH_RETURN_TO_KEY = "legup_auth_return_to";

/* =========================================================
   ROUTE HELPERS
   ========================================================= */

function getDefaultRouteForActor(actor) {
  switch (actor) {
    case "HOMEOWNER":
      return "/hb/dashboard";

    case "LENDER":
      return "/lender/dashboard";

    case "OSS":
      return "/oss/dashboard";

    case "INVESTOR":
      return "/investor/dashboard";

    case "PLATFORM_ADMIN":
      return "/admin/dashboard";

    default:
      return "/";
  }
}

function canActorAccessReturnTo(actor, returnTo) {
  if (!returnTo || typeof returnTo !== "string") {
    return false;
  }

  /*
   * Internal paths only.
   */
  if (!returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return false;
  }

  switch (actor) {
    case "HOMEOWNER":
      return returnTo === "/hb" || returnTo.startsWith("/hb/");

    case "LENDER":
      return returnTo === "/lender" || returnTo.startsWith("/lender/");

    case "OSS":
      return returnTo === "/oss" || returnTo.startsWith("/oss/");

    case "INVESTOR":
      return returnTo === "/investor" || returnTo.startsWith("/investor/");

    case "PLATFORM_ADMIN":
      return returnTo === "/admin" || returnTo.startsWith("/admin/");

    default:
      return false;
  }
}

/* =========================================================
   POST LOGIN REDIRECT
   ========================================================= */

export default function PostLoginRedirectPage() {
  const navigate = useNavigate();

  const { user, isAuthenticated, isLoading, getAccessTokenSilently } =
    useAuth0();

  const hasRun = React.useRef(false);

  React.useEffect(() => {
    const run = async () => {
      /* =====================================================
         WAIT FOR AUTH0
         ===================================================== */

      if (isLoading || !isAuthenticated || hasRun.current) {
        return;
      }

      hasRun.current = true;

      /* =====================================================
         ORIGINAL REQUESTED ROUTE
         ===================================================== */

      const returnTo = window.sessionStorage.getItem(AUTH_RETURN_TO_KEY);

      /* =====================================================
   EMAIL VERIFICATION
   ===================================================== */

      if (user?.email_verified === false) {
        const email = String(user?.email || "").trim();
        const connection =
          window.sessionStorage.getItem(AUTH_CONNECTION_KEY) || "";

        const accountTypeMap = {
          "LEGUP-Homebuyers": "HOMEOWNER",
          "LEGUP-Lenders": "LENDER",
          "LEGUP-OSS": "OSS",
        };

        const accountType = accountTypeMap[connection] || "";

        const params = new URLSearchParams();

        if (email) {
          params.set("email", email);
        }

        if (accountType) {
          params.set("account_type", accountType);
        }

        if (connection) {
          params.set("connection", connection);
        }

        const queryString = params.toString();

        navigate(`/email-unverified${queryString ? `?${queryString}` : ""}`, {
          replace: true,
        });

        return;
      }

      /* =====================================================
         LOAD PLATFORM USER
         ===================================================== */

      try {
        const token = await getAccessTokenSilently();

        const res = await fetch(`${API_BASE}/api/me/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json().catch(() => ({}));

        /* ===================================================
           API ERROR
           =================================================== */

        if (!res.ok) {
          if (res.status >= 500) {
            redirectToServerDown();

            return;
          }

          throw new Error(data?.detail || "Could not load user profile.");
        }

        /* ===================================================
           USER NOT LINKED
           =================================================== */

        if (data?.linked === false) {
          console.error(
            "Authenticated Auth0 user " +
              "is not linked to a " +
              "LEG-UP account.",
            data,
          );

          sessionStorage.removeItem(AUTH_CONNECTION_KEY);

          sessionStorage.removeItem(AUTH_RETURN_TO_KEY);

          navigate("/", {
            replace: true,
          });

          return;
        }

        /* ===================================================
           UPDATE LAST LOGIN
           =================================================== */

        try {
          const loginRes = await fetch(`${API_BASE}/api/auth/login-seen/`, {
            method: "POST",

            headers: {
              Authorization: `Bearer ${token}`,

              "Content-Type": "application/json",
            },
          });

          if (!loginRes.ok) {
            console.warn("Could not update last login.");
          }
        } catch (loginError) {
          console.warn("Last login update failed:", loginError);
        }

        /* ===================================================
           ACTOR
           =================================================== */

        const actor = data?.is_platform_admin
          ? "PLATFORM_ADMIN"
          : data?.primary_actor;

        /* ===================================================
           RESOLVE DESTINATION
           =================================================== */

        const defaultRoute = getDefaultRouteForActor(actor);

        const destination = canActorAccessReturnTo(actor, returnTo)
          ? returnTo
          : defaultRoute;

        sessionStorage.removeItem(AUTH_CONNECTION_KEY);

        sessionStorage.removeItem(AUTH_RETURN_TO_KEY);

        navigate(destination, {
          replace: true,
        });
      } catch (error) {
        console.error("Post-login redirect failed:", error);

        redirectToServerDown();
      }
    };

    run();
  }, [user, isAuthenticated, isLoading, getAccessTokenSilently, navigate]);

  return <FullScreenLoader text="Preparing your workspace..." />;
}
